import LockIcon from '@mui/icons-material/Lock'
import { ButtonBase, Drawer, styled, Theme, ThemeProvider, Tooltip, useMediaQuery } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import CloseIcon from '../../../assets/icons/emotes/close.svg?react'
import ToggleOffIcon from '../../../assets/icons/emotes/toggle-off.svg?react'
import ToggleOnIcon from '../../../assets/icons/emotes/toggle-on.svg?react'
import { isGuest, isSubscribed } from '../../../helpers'
import { setEmotesMuted, useEmotesMuted } from '../../../sharedComponents/src/settings/emotesMuted'
import { useStoreActions, useStoreState } from '../../../store/hooks'
import { splitEmote } from './EmoteBubble'
import { useEmoteCooldown } from './EmoteButton'
import { EmoteMessage, selectEmotesAvailable } from './EmotesModel'

// Figma "Emote Drawer 2". Above the result dialog (1300) and the game confirmation dialogs (1601).
const PICKER_Z_INDEX = 1700
// How long a tapped locked tile keeps its unlock tooltip.
const HINT_MS = 2000
type EmoteAccess = 'guest' | 'member' | 'subscriber'

// The tooltip text for a locked emote, or undefined when this user can send it.
const getLockMessage = (message: EmoteMessage, access: EmoteAccess) => {
    if (access === 'guest' && !message.isGuestEnabled) {
        return message.isMemberEnabled ? 'Join for Free to Unlock' : 'Subscribe to Unlock'
    }
    if (access === 'member' && !message.isMemberEnabled) return 'Subscribe to Unlock'
    return undefined
}

// Emotes this user can never unlock (none of their reachable tiers has it) are left out of the grid.
const isReachable = (message: EmoteMessage, access: EmoteAccess) =>
    message.isSubscribedEnabled ||
    (access !== 'subscriber' && message.isMemberEnabled) ||
    (access === 'guest' && message.isGuestEnabled)

// MUI tooltips sit at 1500, under the picker; lift them above it while it is open.
const tooltipAbovePicker = (theme: Theme) => ({ ...theme, zIndex: { ...theme.zIndex, tooltip: PICKER_Z_INDEX + 1 } })
// Pull the tooltip in from MUI's default 14px gap above the tile, as HelpTooltip does: 4px on desktop, 2px on mobile.
const TOOLTIP_OFFSET_DESKTOP = -10
const TOOLTIP_OFFSET_MOBILE = -18
const getTooltipSlotProps = (offset: number) => ({
    popper: { modifiers: [{ name: 'offset', options: { offset: [0, offset] } }] },
})

const paperSx = {
    '&.MuiDrawer-paper': {
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '7px',
        width: '100%',
        maxWidth: '420px',
        margin: '0 auto',
        padding: '12px 24px calc(12px + env(safe-area-inset-bottom))',
        backgroundColor: '#131D26',
        backgroundImage: 'none',
        border: '2px solid white',
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0',
    },
}

const CloseButton = styled(ButtonBase)({
    position: 'absolute',
    top: '10px',
    right: '8px',
    width: '34px',
    height: '26px',
})

const Title = styled('h2')({
    margin: 0,
    fontFamily: 'Fira Sans',
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '24px',
    textTransform: 'uppercase',
    color: 'white',
})

const Grid = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '4px',
    width: '100%',
})

const Tile = styled(ButtonBase)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
    width: '100%',
    minWidth: 0,
    height: '44px',
    padding: '0 4px',
    borderRadius: '8px',
    backgroundColor: '#2F3941',
    filter: 'drop-shadow(0px 2px 0px rgba(0, 0, 0, 0.4))',
    color: 'white',
    fontFamily: 'Space Grotesk',
    fontSize: '16px',
    whiteSpace: 'nowrap',
    fontFeatureSettings: '"salt" 1, "ss02" 1, "liga" 0',
    '&.Mui-disabled, &.locked': { opacity: 0.45 },
    '&.locked': { cursor: 'default' },
})

// Purely a visual hint; the whole locked tile is the tooltip trigger (the icon alone is too small to tap).
const LockBadge = styled(LockIcon)({
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '1rem',
    height: '1rem',
    pointerEvents: 'none',
})

const TileEmoji = styled('span')({ fontWeight: 700, lineHeight: '18px' })

const TileLabel = styled('span')({
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 300,
    lineHeight: '16px',
})

const MuteSwitch = styled(ButtonBase)({
    gap: '12px',
    color: '#9FA3A6',
    fontFamily: 'Space Grotesk',
    fontSize: '14px',
    lineHeight: 1.4,
    fontFeatureSettings: '"salt" 1, "ss02" 1, "liga" 0',
})

const Toggle = styled('span')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '32px',
    svg: { transform: 'scaleX(-1)' },
})

export const EmotePicker = () => {
    const open = useStoreState((state) => state.emotes.pickerOpen)
    const available = useStoreState(selectEmotesAvailable)
    const messages = useStoreState((state) => state.emotes.messages)
    const pending = useStoreState((state) => !!state.emotes.pending)
    const cooldown = useEmoteCooldown()
    const muted = useEmotesMuted()
    const access = useStoreState((state): EmoteAccess => {
        const groups = state.userData.userData?.groups
        if (isSubscribed(groups)) return 'subscriber'
        return isGuest(groups) ? 'guest' : 'member'
    })

    const setPickerOpen = useStoreActions((actions) => actions.emotes.setPickerOpen)
    const sendEmote = useStoreActions((actions) => actions.emotes.sendEmote)

    const close = () => setPickerOpen(false)

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
    const tooltipSlotProps = getTooltipSlotProps(isMobile ? TOOLTIP_OFFSET_MOBILE : TOOLTIP_OFFSET_DESKTOP)

    // The locked tile whose unlock tooltip is showing.
    const [hintId, setHintId] = useState<string>()
    const hintTimer = useRef<ReturnType<typeof setTimeout>>()

    const showHint = (id: string) => {
        setHintId(id)
        clearTimeout(hintTimer.current)
        hintTimer.current = setTimeout(() => setHintId(undefined), HINT_MS)
    }

    useEffect(() => {
        if (!open) setHintId(undefined)
    }, [open])

    useEffect(() => () => clearTimeout(hintTimer.current), [])

    return (
        <ThemeProvider theme={tooltipAbovePicker}>
            <Drawer
                anchor="bottom"
                open={open && available}
                onClose={close}
                sx={{ zIndex: PICKER_Z_INDEX }}
                PaperProps={{ sx: paperSx }}
            >
                <CloseButton aria-label="Close" onClick={close}>
                    <CloseIcon />
                </CloseButton>
                <Title>Emotes</Title>
                <Grid>
                    {messages
                        .filter((message) => isReachable(message, access))
                        .map((message) => {
                            const { emoji, label } = splitEmote(message.content)
                            const lockMessage = getLockMessage(message, access)
                            const locked = !!lockMessage
                            return (
                                // A locked tile stays enabled (a disabled one ignores taps) so tapping it shows the tooltip.
                                // The tap opens it from onClick: MUI's touch timers race Android's emulated mouse events.
                                <Tooltip
                                    key={message.id}
                                    title={lockMessage ?? ''}
                                    placement="top"
                                    open={locked && hintId === message.id}
                                    onOpen={() => locked && setHintId(message.id)}
                                    onClose={() => setHintId((id) => (id === message.id ? undefined : id))}
                                    disableTouchListener
                                    disableFocusListener
                                    slotProps={tooltipSlotProps}
                                >
                                    <Tile
                                        className={locked ? 'locked' : undefined}
                                        aria-label={locked ? `${message.content} (locked)` : message.content}
                                        aria-disabled={locked}
                                        disableRipple={locked}
                                        disabled={!locked && (pending || cooldown > 0)}
                                        onClick={() => {
                                            if (locked) {
                                                showHint(message.id)
                                                return
                                            }
                                            sendEmote(message.id)
                                            close()
                                        }}
                                    >
                                        {emoji && <TileEmoji>{emoji}</TileEmoji>}
                                        {label && <TileLabel>{label}</TileLabel>}
                                        {locked && <LockBadge color="secondary" />}
                                    </Tile>
                                </Tooltip>
                            )
                        })}
                </Grid>
                <MuteSwitch role="switch" aria-checked={muted} onClick={() => setEmotesMuted(!muted)}>
                    Mute Opponent
                    <Toggle>{muted ? <ToggleOnIcon /> : <ToggleOffIcon />}</Toggle>
                </MuteSwitch>
            </Drawer>
        </ThemeProvider>
    )
}
